import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { compareByTransporterLineNumber } from "encamp-shared/src/waste/manifestSourcePriority";
import {
  ManifestComplianceEscalation,
  SiteManagerAccessStatus,
  WasteManifestSource,
} from "generated-graphql/graphql";

import { SOURCE_LABEL, SOURCE_ORDER } from "../manifestSourceDisplay";
import { DiscrepancyTallyView } from "./discrepancyBreakdown";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The UTC-midnight epoch of a timestamp (calendar-day anchor). */
function utcMidnight(epochMs: number): number {
  const date = new Date(epochMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Calendar days between two ISO dates (UTC); null when either side is missing.
 * Both ends are anchored to UTC midnight so timestamps with a time-of-day
 * component (vendor ingests parse local-time dates) can't shift a manifest
 * across the 45/60-day compliance boundaries — matches the API's
 * `daysSinceShipment` math exactly. (encamp-shared's `getDurationDays` rounds
 * raw deltas instead, so it is deliberately not reused here.)
 */
export function daysBetweenUtc(
  fromIso: string | null | undefined,
  toIso: string | null | undefined
): number | null {
  if (!fromIso || !toIso) return null;
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((utcMidnight(to) - utcMidnight(from)) / MS_PER_DAY);
}

function addDaysIso(iso: string, days: number): string {
  return new Date(Date.parse(iso) + days * MS_PER_DAY).toISOString();
}

/**
 * Clamp a day count to non-negative. A negative value means dates are out of
 * order (e.g. a future ship date, or a certification dated before shipment from
 * a bad import) — surfacing "-3 days" to the user is nonsense, so floor at 0.
 * Preserves null (unknown stays unknown).
 */
function clampDays(days: number | null): number | null {
  return days == null ? null : Math.max(0, days);
}

/** Drives the encircled bullet icon + color in the Summary list. */
export type StatusLevel = "ok" | "warning" | "error" | "info" | "neutral";

export interface SummaryItem {
  key: string;
  /** Human-readable, sentence-style status (the whole line the user reads). */
  message: string;
  level: StatusLevel;
  /** Optional explanation shown via an info icon at the end of the line. */
  tooltip?: string;
}

/** Why RCRA site manager access matters — shown on the site-manager-access line. */
const SITE_MANAGER_ACCESS_TOOLTIP =
  "RCRA site manager access is granted per facility EPA ID, not per manifest. " +
  "Without it, we can't pull RCRAInfo data for this EPA ID sooner than 90 days " +
  "(from the public export), and can't guarantee a timely response to EPA " +
  "correction requests.";

export interface SummaryInput {
  availableSources: readonly WasteManifestSource[];
  /**
   * Per-EPA-ID RCRA site-manager access (a site-level fact). Drives the access
   * line — NOT `isRegulatedEManifest`, which is the per-manifest e-Manifest link
   * and was being mislabeled as an access failure on vendor-only manifests.
   */
  siteManagerAccess: SiteManagerAccessStatus | null | undefined;
  /**
   * Whether this is a uniform hazardous waste manifest. RCRA site-manager access
   * only applies to e-Manifest-eligible (uniform haz) manifests, so the access
   * line is shown only when this is true — non-haz vendor manifests omit it
   * entirely rather than render an inapplicable access state.
   */
  isUniformHazardousManifest: boolean | null | undefined;
  /** Open-discrepancy counts split by kind (compliance vs field). */
  discrepancyTally: DiscrepancyTallyView;
  daysSinceShipment: number | null | undefined;
  hasEManifest: boolean | null | undefined;
  /** Whether an exception report submission has been started/filed. */
  hasExceptionReport: boolean | null | undefined;
  exceptionReportFiled: boolean | null | undefined;
  /** Reported federal generator status; VSQG/non-generators aren't subject to upload rules. */
  reportedWasteGeneratorStatus: string | null | undefined;
  /** Whether the transporter/TSDF has been contacted (proof of contact recorded). */
  contacted: boolean | null | undefined;
  /**
   * Highest open eManifestMissing escalation from the pipeline's stored
   * discrepancies (the manifest row's `eManifestComplianceEscalation`). This —
   * never the day count — is what triggers the 45/60-day obligation lines.
   */
  escalation: ManifestComplianceEscalation | null | undefined;
  /** Ship + certification dates — drive the late-certification (delinquent upload) rules. */
  shippedDate?: string | null;
  eManifestCertifiedDate?: string | null;
}

/**
 * Generator statuses that are NOT subject to the 45/60-day e-Manifest upload
 * obligation (so no exception report can be required). Unknown/null status is
 * treated conservatively as potentially subject.
 */
const NOT_SUBJECT_TO_EMANIFEST_UPLOAD = new Set<string>([
  "VERY_SMALL_QUANTITY_GENERATOR",
  "NOT_A_GENERATOR",
]);

export function isSubjectToEManifestUpload(status: string | null | undefined): boolean {
  return !NOT_SUBJECT_TO_EMANIFEST_UPLOAD.has(status ?? "");
}

/** Number of days past shipment at which a 45-day contact obligation kicks in. */
const CONTACT_DEADLINE_DAYS = 45;
/** Number of days past shipment at which an exception report is required. */
const EXCEPTION_DEADLINE_DAYS = 60;

/** Human label for the generator statuses that aren't subject to upload rules. */
export const NOT_SUBJECT_LABEL: Record<string, string> = {
  VERY_SMALL_QUANTITY_GENERATOR: "a VSQG",
  NOT_A_GENERATOR: "not a hazardous waste generator",
};

const pluralize = (n: number, singular: string, plural: string) => (n === 1 ? singular : plural);

export function dataSourcesSummary(availableSources: readonly WasteManifestSource[]): SummaryItem {
  const present = SOURCE_ORDER.filter((s) => availableSources.includes(s));
  const presentLabels = present.map((s) => SOURCE_LABEL[s]).join(", ");

  if (present.length === 0) {
    return { key: "dataSources", message: "No data sources available", level: "error" };
  }
  if (present.length === SOURCE_ORDER.length) {
    return {
      key: "dataSources",
      message: `All data sources present: ${presentLabels}`,
      level: "ok",
    };
  }
  const missing = SOURCE_ORDER.filter((s) => !availableSources.includes(s)).map(
    (s) => SOURCE_LABEL[s]
  );
  // Missing e-Manifest (the source of truth) is the noteworthy gap.
  const eManifestMissing = !availableSources.includes(WasteManifestSource.EManifest);
  return {
    key: "dataSources",
    message: `Missing ${missing.join(", ")} data — have ${presentLabels}`,
    level: eManifestMissing ? "warning" : "info",
  };
}

/**
 * The site-manager-access line, driven by the per-EPA-ID access fact. UNKNOWN
 * (never probed / no EPA ID) reads as a neutral "not determined" rather than a
 * missing-access failure — we have no access record either way, so we don't
 * assert one. The per-manifest "no e-Manifest record" fact is carried
 * separately by the data-sources line.
 */
export function siteManagerAccessSummary(
  siteManagerAccess: SiteManagerAccessStatus | null | undefined
): SummaryItem {
  if (siteManagerAccess === SiteManagerAccessStatus.Granted) {
    return {
      key: "siteManagerAccess",
      message: "RCRA site manager access granted",
      level: "ok",
      tooltip: SITE_MANAGER_ACCESS_TOOLTIP,
    };
  }
  if (siteManagerAccess === SiteManagerAccessStatus.None) {
    return {
      key: "siteManagerAccess",
      message: "No RCRA site manager access for this EPA ID",
      level: "warning",
      tooltip: `${SITE_MANAGER_ACCESS_TOOLTIP} Reach out to your Encamp contact to request access.`,
    };
  }
  return {
    key: "siteManagerAccess",
    message: "RCRA site manager access not determined",
    level: "info",
    tooltip: SITE_MANAGER_ACCESS_TOOLTIP,
  };
}

/**
 * Whether to warn that an overdue e-Manifest can't be retrieved without RCRA
 * site-manager access. True when this is an e-Manifest-eligible (uniform haz)
 * manifest that is subject to upload, is past the exception deadline with no
 * e-Manifest on file, and we lack site-manager access — so we can't pull it
 * from RCRAInfo until it lands in the public export ~90 days after shipment.
 * Granted access (we can pull it) or an e-Manifest we already have means there
 * is nothing to warn about. Unknown access still warns: without a confirmed
 * grant we have no way in.
 *
 * The past-the-deadline test reads the pipeline's stored `Overdue_60`
 * escalation, never the raw day count — same as the compliance lines. Deriving
 * it from `daysSinceShipment` here would fire the warning on a manifest the
 * pipeline decided isn't subject at all (its classification/tracking-number
 * gating never stored an escalation), contradicting the "not subject"
 * compliance panel beside it. The trade is the same one the rest of this file
 * accepts: the warning lags a pipeline run rather than firing the instant the
 * count crosses 60.
 */
export function shouldWarnMissingEManifestSiteAccess(input: {
  isUniformHazardousManifest: boolean | null | undefined;
  reportedWasteGeneratorStatus: string | null | undefined;
  hasEManifest: boolean | null | undefined;
  escalation: ManifestComplianceEscalation | null | undefined;
  siteManagerAccess: SiteManagerAccessStatus | null | undefined;
}): boolean {
  if (!input.isUniformHazardousManifest) return false;
  if (!isSubjectToEManifestUpload(input.reportedWasteGeneratorStatus)) return false;
  if (input.hasEManifest) return false;
  if (input.siteManagerAccess === SiteManagerAccessStatus.Granted) return false;
  return input.escalation === ManifestComplianceEscalation.Overdue_60;
}

export function discrepanciesSummary(tally: DiscrepancyTallyView): SummaryItem {
  const { complianceTotal, fieldTotal, fieldUnresolved } = tally;
  const total = complianceTotal + fieldTotal;
  if (total === 0) {
    return { key: "discrepancies", message: "No discrepancies detected", level: "ok" };
  }
  // Break the total down by source of the flag (matches the list view's two
  // bubbles): EPA-flagged = compliance issues, Encamp-flagged = field
  // discrepancies. Not by unresolved/pending.
  const parts: string[] = [];
  if (complianceTotal > 0) parts.push(`${complianceTotal} EPA-flagged`);
  if (fieldTotal > 0) parts.push(`${fieldTotal} Encamp-flagged`);
  return {
    key: "discrepancies",
    message: `${total} ${pluralize(total, "discrepancy", "discrepancies")}: ${parts.join(", ")}`,
    // Pending-only field discrepancies read as info; anything needing action is warning.
    level: complianceTotal > 0 || fieldUnresolved > 0 ? "warning" : "info",
  };
}

/** The distinct e-Manifest upload compliance states. */
export type ComplianceLineKind =
  | "onTrack"
  | "notSubject"
  | "uploaded"
  | "contactNeeded"
  | "contacted"
  | "exceptionRequired"
  | "exceptionStarted"
  | "exceptionFiled";

export interface ComplianceLine {
  kind: ComplianceLineKind;
  /** One-line summary message + status level (drives the Summary list). */
  message: string;
  level: StatusLevel;
  /**
   * Action lines split the message into a short bold lead ("TSDF contact
   * overdue") and a regular-weight remainder, for the Compliance section's
   * callout rendering. `message` stays the joined form for the Summary list.
   */
  title?: string;
  detail?: string;
}

/** An action-required line: bold lead + regular detail, message = the joined pair. */
function actionLine(
  kind: ComplianceLineKind,
  level: StatusLevel,
  title: string,
  detail: string
): ComplianceLine {
  return { kind, level, title, detail, message: `${title} — ${detail}` };
}

export interface ComplianceLinesInput {
  subjectToUpload: boolean;
  /** Label for a not-subject facility (e.g. "a VSQG"), when applicable. */
  notSubjectLabel?: string | null;
  /**
   * Highest open eManifestMissing escalation from the pipeline's stored
   * discrepancies — the single trigger for the 45/60-day obligation lines.
   */
  escalation: ManifestComplianceEscalation | null | undefined;
  /**
   * Whether this is a uniform hazardous waste manifest (pipeline-computed).
   * With no escalation, a non-uniform manifest reads as not subject rather
   * than "on track" — it can never appear in e-Manifest at all.
   */
  isUniformHazardousManifest: boolean | null | undefined;
  hasEManifest: boolean;
  /** Display only — the day count shown in messages, never the trigger. */
  daysSinceShipment: number;
  /** Transporter/TSDF contacted (proof of contact recorded). */
  contacted: boolean;
  exceptionReportStatus: "none" | "started" | "filed";
  /** Days from shipment to e-Manifest certification, when both dates are known. */
  daysToCertification?: number | null;
  /** Certification date, for display on the uploaded line. */
  eManifestCertifiedDate?: string | null;
}

/**
 * The e-Manifest upload compliance state, as an ordered list of lines — the
 * single source of truth shared by the Summary (one-liners) and the Compliance
 * section (rich bullets), so the two never disagree.
 *
 * Escalation-driven: the 45-day contact and 60-day exception-report obligations
 * come from the pipeline's stored eManifestMissing discrepancy (`escalation`),
 * which already gates on uniform tracking number, SQG/LQG status, and waste-code
 * classification — never re-derived here from the day count, which would flag
 * manifests the pipeline determined aren't subject at all. The trade: a manifest
 * crossing day 45 reads "on track" until the next pipeline run. Day counts in
 * the messages stay live for display. Escalation trumps the uniform-hazardous
 * flag when they disagree (a stored obligation is never hidden); with no
 * escalation, a non-uniform manifest reads as not subject.
 *
 * Once an e-Manifest lands the discrepancy auto-resolves, so the late-upload
 * (delinquent certification) lines stay date-derived from the certification
 * date. A contact already made keeps its acknowledgment line through the later
 * stages (exception required/started/filed) — it's the first half of the
 * obligation and stays visible, matching the compliance timeline.
 */
export function getComplianceLines(input: ComplianceLinesInput): ComplianceLine[] {
  const { subjectToUpload, notSubjectLabel, hasEManifest, daysSinceShipment, contacted } = input;

  if (!subjectToUpload) {
    return [
      {
        kind: "notSubject",
        level: "ok",
        message: notSubjectLabel
          ? `Not subject to e-Manifest upload requirements — facility is ${notSubjectLabel}`
          : "Not subject to e-Manifest upload requirements",
      },
    ];
  }
  if (hasEManifest) {
    const daysToCertification = input.daysToCertification ?? null;
    // "(X days)" = shipment → certification, so the reader sees how close the
    // upload came to the 45/60-day deadlines without doing date math.
    const durationSuffix =
      daysToCertification != null
        ? ` (${daysToCertification} ${daysToCertification === 1 ? "day" : "days"})`
        : "";
    const certifiedLabel = input.eManifestCertifiedDate
      ? `e-Manifest certified ${getUtcDisplayDate(input.eManifestCertifiedDate)}${durationSuffix}`
      : "e-Manifest uploaded";
    const delinquent =
      daysToCertification != null && daysToCertification >= EXCEPTION_DEADLINE_DAYS;
    const filed = input.exceptionReportStatus === "filed";
    const started = input.exceptionReportStatus === "started";

    // The uploaded line only claims "no exception-reporting obligation" when
    // the upload was on time AND no report exists — otherwise the report
    // line/obligation below carries the state, so the header stays neutral.
    let uploadedMessage = certifiedLabel;
    let uploadedLevel: StatusLevel = "ok";
    if (delinquent) {
      uploadedMessage = `${certifiedLabel} — uploaded after the 60-day deadline`;
      uploadedLevel = "info";
    } else if (!filed && !started) {
      uploadedMessage = `${certifiedLabel} — no exception-reporting obligation`;
    }

    const lines: ComplianceLine[] = [
      { kind: "uploaded", level: uploadedLevel, message: uploadedMessage },
    ];
    // Compliance evidence stays visible regardless of certification timing — a
    // recorded contact and a filed/started report don't disappear just because
    // the upload eventually landed (and before day 60).
    if (contacted) lines.push({ kind: "contacted", level: "ok", message: "TSDF contacted" });
    if (filed) {
      lines.push({ kind: "exceptionFiled", level: "info", message: "Exception report filed" });
    } else if (started) {
      lines.push({
        kind: "exceptionStarted",
        level: "warning",
        message: "Exception report in progress",
      });
    } else if (delinquent) {
      // Late upload with no report: the obligation survives the (late) upload.
      lines.push(
        actionLine(
          "exceptionRequired",
          "error",
          "Exception report required",
          `e-Manifest was certified ${daysToCertification} days after shipment`
        )
      );
    }
    return lines;
  }
  const contactedLine: ComplianceLine = {
    kind: "contacted",
    level: "ok",
    message: "TSDF contacted",
  };

  if (input.exceptionReportStatus === "filed") {
    // Chronological: the contact (when made) preceded the filing.
    return [
      ...(contacted ? [contactedLine] : []),
      { kind: "exceptionFiled", level: "info", message: "Exception report filed" },
    ];
  }
  if (input.exceptionReportStatus === "started") {
    return [
      ...(contacted ? [contactedLine] : []),
      { kind: "exceptionStarted", level: "warning", message: "Exception report in progress" },
    ];
  }

  const days = daysSinceShipment;
  const contactNeeded = actionLine(
    "contactNeeded",
    "warning",
    "TSDF contact overdue",
    `${days} days since shipment, no e-Manifest`
  );

  if (input.escalation === ManifestComplianceEscalation.Overdue_60) {
    const lines: ComplianceLine[] = [
      actionLine(
        "exceptionRequired",
        "error",
        "Exception report required",
        `${days} days since shipment, no e-Manifest`
      ),
    ];
    // The outstanding action leads; a contact already made trails as an
    // acknowledgment, an unmade one as the second outstanding action.
    lines.push(contacted ? contactedLine : contactNeeded);
    return lines;
  }
  if (input.escalation === ManifestComplianceEscalation.Overdue_45) {
    return contacted ? [contactedLine] : [contactNeeded];
  }
  // No open escalation. A non-uniform manifest isn't in e-Manifest's universe
  // at all, so "on track" (implying a countdown) would misread — say why
  // there's no obligation instead.
  if (!input.isUniformHazardousManifest) {
    return [
      {
        kind: "notSubject",
        level: "ok",
        message:
          "Not subject to e-Manifest upload requirements — not a uniform hazardous waste manifest",
      },
    ];
  }
  // No obligation yet, but a contact already made is still valid evidence —
  // keep its acknowledgment rather than dropping it.
  return [
    {
      kind: "onTrack",
      level: "ok",
      message: `On track — ${days} days since shipment, no e-Manifest yet`,
    },
    ...(contacted ? [contactedLine] : []),
  ];
}

function exceptionReportStatusOf(input: SummaryInput): "none" | "started" | "filed" {
  if (input.exceptionReportFiled) return "filed";
  if (input.hasExceptionReport) return "started";
  return "none";
}

/** Build the shared {@link ComplianceLinesInput} from a SummaryInput. */
export function complianceLinesInputFromSummary(input: SummaryInput): ComplianceLinesInput {
  return {
    subjectToUpload: isSubjectToEManifestUpload(input.reportedWasteGeneratorStatus),
    notSubjectLabel: NOT_SUBJECT_LABEL[input.reportedWasteGeneratorStatus ?? ""],
    escalation: input.escalation ?? null,
    isUniformHazardousManifest: input.isUniformHazardousManifest,
    hasEManifest: !!input.hasEManifest,
    daysSinceShipment: Math.max(0, input.daysSinceShipment ?? 0),
    contacted: !!input.contacted,
    exceptionReportStatus: exceptionReportStatusOf(input),
    daysToCertification: clampDays(daysBetweenUtc(input.shippedDate, input.eManifestCertifiedDate)),
    eManifestCertifiedDate: input.eManifestCertifiedDate,
  };
}

export function buildSummaryItems(input: SummaryInput): SummaryItem[] {
  // Compliance status (possibly several lines) sits right below site-level
  // access; discrepancies last.
  const complianceItems: SummaryItem[] = getComplianceLines(
    complianceLinesInputFromSummary(input)
  ).map((line) => ({ key: `compliance-${line.kind}`, message: line.message, level: line.level }));

  // Site-manager access only applies to e-Manifest-eligible (uniform haz)
  // manifests; non-haz vendor manifests omit the line rather than render an
  // inapplicable access state.
  const accessItems = input.isUniformHazardousManifest
    ? [siteManagerAccessSummary(input.siteManagerAccess)]
    : [];

  return [
    dataSourcesSummary(input.availableSources),
    ...accessItems,
    ...complianceItems,
    discrepanciesSummary(input.discrepancyTally),
  ];
}

export interface TimelineEvent {
  key: string;
  label: string;
  /** Short lines under the label (a date, a day-count, a transporter); "—" when pending. */
  captionLines: string[];
  done: boolean;
}

export interface TimelineTransporter {
  transporterLineNumber: string;
  transporterName?: string | null;
  transporterId?: string | null;
  daysInPossession?: number | null;
}

export interface TimelineInput {
  shippedDate: string | null | undefined;
  receivedDate: string | null | undefined;
  /** Authoritative shipped→received day count when the API provides one. */
  eManifestDaysInTransit?: number | null;
  /** Fallback transit counter while the shipment is still on the road. */
  daysSinceShipment?: number | null;
  transporters?: readonly TimelineTransporter[];
}

const daysLabel = (n: number | null | undefined) =>
  n == null ? "—" : `${n} ${n === 1 ? "day" : "days"}`;

/** First two words of the transporter name; EPA ID (or line number) as fallback. */
function transporterShortLabel(transporter: TimelineTransporter): string {
  const words = transporter.transporterName?.trim().split(/\s+/) ?? [];
  if (words.length > 0 && words[0] !== "") return words.slice(0, 2).join(" ");
  return transporter.transporterId ?? `Transporter ${transporter.transporterLineNumber}`;
}

/**
 * The shipping lifecycle as a flow: Shipped → In Transit → Received by TSDF.
 * The In Transit stage shows a day count instead of a date; with more than one
 * transporter it expands to one stage per transporter (identified by the first
 * two words of its name), each showing that leg's days in possession.
 * Compliance events (contact / exception report / certification) live on the
 * e-Manifest Upload Compliance timeline, not here.
 */
export function buildTimelineEvents(input: TimelineInput): TimelineEvent[] {
  const { shippedDate, receivedDate } = input;
  const transporters = input.transporters ?? [];

  const daysInTransit = clampDays(
    input.eManifestDaysInTransit ??
      (receivedDate != null
        ? daysBetweenUtc(shippedDate, receivedDate)
        : (input.daysSinceShipment ?? null))
  );

  const events: TimelineEvent[] = [
    {
      key: "shipped",
      label: "Shipped",
      captionLines: [shippedDate ? getUtcDisplayDate(shippedDate) : "—"],
      done: shippedDate != null,
    },
  ];

  if (transporters.length > 1) {
    const sorted = [...transporters].sort(compareByTransporterLineNumber);
    for (const transporter of sorted) {
      events.push({
        key: `inTransit-${transporter.transporterLineNumber}`,
        label: "In Transit",
        captionLines: [transporterShortLabel(transporter), daysLabel(transporter.daysInPossession)],
        // A leg is complete once its possession days are recorded, or once the
        // TSDF has the shipment (legs can't still be in progress after receipt).
        done: transporter.daysInPossession != null || receivedDate != null,
      });
    }
  } else {
    events.push({
      key: "inTransit",
      label: "In Transit",
      captionLines: [daysLabel(shippedDate != null ? daysInTransit : null)],
      done: receivedDate != null,
    });
  }

  events.push({
    key: "received",
    label: "Received by TSDF",
    captionLines: [receivedDate ? getUtcDisplayDate(receivedDate) : "—"],
    done: receivedDate != null,
  });

  return events;
}

export interface ComplianceTimelineInput {
  subjectToUpload: boolean;
  /**
   * Whether this is a uniform hazardous waste manifest (pipeline-computed).
   * With no escalation and no certification history, a non-uniform manifest
   * hides the timeline even when compliance evidence was recorded — the lines
   * read "not subject", and obligation steps alongside would contradict them.
   */
  isUniformHazardousManifest: boolean | null | undefined;
  shippedDate: string | null | undefined;
  /**
   * Highest open eManifestMissing escalation from the pipeline's stored
   * discrepancies — what marks the 45-day window as crossed while the
   * e-Manifest is still missing.
   */
  escalation: ManifestComplianceEscalation | null | undefined;
  eManifestCertifiedDate: string | null | undefined;
  /** Transporter/TSDF contacted (proof of contact recorded). */
  contacted: boolean;
  /** When the contact happened, if a timestamp was recorded. */
  contactedAt?: string | null;
  exceptionReportFiled: boolean;
  /**
   * An exception report exists but isn't filed yet. Keeps the Exception Report
   * step visible (as "In progress") so a started-but-unfiled report isn't shown
   * as an unmet future obligation.
   */
  exceptionReportStarted?: boolean;
  /** When the exception report was filed — set only once it's filed. */
  exceptionFiledDate?: string | null;
}

/**
 * The e-Manifest upload compliance obligations as their own mini-timeline:
 * Contact Transporter (45 days) → Exception Report (60 days) → e-Manifest
 * certified. Returns [] (timeline hidden) unless the 45-day window was
 * actually crossed without an e-Manifest — the pipeline raised an open
 * eManifestMissing escalation (never re-derived from the day count), or
 * certification came 45+ days after shipment (a late upload; the resolved
 * discrepancy no longer carries an escalation, so this leg stays date-derived)
 * — OR a compliance action was actually recorded (contact / filed report), so
 * that evidence never silently disappears when an early certification lands.
 * The Exception Report step is dropped only when certification landed before
 * the 60-day mark AND no report was filed (the obligation never matured); a
 * certification on/after day 60, or any filed report, keeps it.
 */
export function buildComplianceTimeline(input: ComplianceTimelineInput): TimelineEvent[] {
  const { shippedDate, eManifestCertifiedDate } = input;
  if (!input.subjectToUpload || !shippedDate) return [];

  const daysToCertification = daysBetweenUtc(shippedDate, eManifestCertifiedDate);
  // Non-uniform with no stored escalation and no certification history reads
  // "not subject" in the lines (e.g. a subject → not-subject transition after a
  // waste-code correction, with a proof-of-contact left behind) — mirror that
  // here rather than render contradictory obligation steps. A stored
  // escalation still wins over a lagging flag, same as getComplianceLines.
  if (
    input.escalation == null &&
    daysToCertification == null &&
    !input.isUniformHazardousManifest
  ) {
    return [];
  }
  const crossed45 =
    daysToCertification != null
      ? daysToCertification >= CONTACT_DEADLINE_DAYS
      : input.escalation != null;
  // Show the timeline if the window was crossed OR a compliance action was
  // recorded — a contact/started/filed report made before day 45 must still be
  // visible.
  if (
    !crossed45 &&
    !input.contacted &&
    !input.exceptionReportFiled &&
    !input.exceptionReportStarted
  )
    return [];

  let contactCaption: string;
  if (input.contacted) {
    contactCaption = input.contactedAt ? getUtcDisplayDate(input.contactedAt) : "Contacted";
  } else {
    contactCaption = `after ${getUtcDisplayDate(addDaysIso(shippedDate, CONTACT_DEADLINE_DAYS))}`;
  }

  const events: TimelineEvent[] = [
    {
      key: "contactTransporter",
      label: "Contact Transporter",
      captionLines: [contactCaption],
      done: input.contacted,
    },
  ];

  const certifiedBefore60 =
    daysToCertification != null && daysToCertification < EXCEPTION_DEADLINE_DAYS;
  // Keep the Exception Report step unless the upload was on-time (<60 days)
  // AND no report was filed or started — a filed/started report stays visible
  // regardless of certification timing.
  if (!certifiedBefore60 || input.exceptionReportFiled || input.exceptionReportStarted) {
    let exceptionCaption: string;
    if (input.exceptionReportFiled) {
      exceptionCaption = input.exceptionFiledDate
        ? getUtcDisplayDate(input.exceptionFiledDate)
        : "Filed";
    } else if (input.exceptionReportStarted) {
      exceptionCaption = "In progress";
    } else {
      exceptionCaption = `after ${getUtcDisplayDate(
        addDaysIso(shippedDate, EXCEPTION_DEADLINE_DAYS)
      )}`;
    }
    events.push({
      key: "exceptionReport",
      label: "Exception Report",
      captionLines: [exceptionCaption],
      done: input.exceptionReportFiled,
    });
  }

  events.push({
    key: "eManifestCertified",
    label: "e-Manifest certified",
    captionLines: [eManifestCertifiedDate ? getUtcDisplayDate(eManifestCertifiedDate) : "—"],
    done: eManifestCertifiedDate != null,
  });

  return events;
}
